// A layout preset drawn as an SVG rect composition straight from PRESET_ZONES —
// not an icon, so the diagram can never drift from the real geometry. Used as the
// selectable tile in the create/edit picker and as a small glyph in the list.
// The main zone reads as the "content" fill; side/ticker zones as accented panels.

import { PRESET_ZONES, ZONE_LABELS, type LayoutPreset } from '../../lib/layout-presets.ts';

// 16:9 stage, matching the 1920×1080 authoring canvas the rects are a percent of.
const VW = 160;
const VH = 90;
const GAP = 1.4; // visual gutter between zones (SVG units)

export function PresetDiagram({
  preset,
  showLabels = false,
  className,
}: {
  preset: LayoutPreset;
  showLabels?: boolean;
  className?: string;
}) {
  const zones = PRESET_ZONES[preset];
  return (
    <svg
      className={`preset-diagram${className ? ` ${className}` : ''}`}
      viewBox={`0 0 ${VW} ${VH}`}
      role="img"
      aria-label={`${preset} layout`}
      preserveAspectRatio="xMidYMid meet"
    >
      <rect x={0} y={0} width={VW} height={VH} rx={3} className="preset-diagram-bg" />
      {zones.map((z) => {
        const x = (z.rect.x / 100) * VW + GAP / 2;
        const y = (z.rect.y / 100) * VH + GAP / 2;
        const w = (z.rect.w / 100) * VW - GAP;
        const h = (z.rect.h / 100) * VH - GAP;
        return (
          <g key={z.zone}>
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              rx={2}
              className={`preset-zone preset-zone-${z.zone}`}
            />
            {showLabels && w > 22 && h > 12 && (
              <text x={x + w / 2} y={y + h / 2} className="preset-zone-label" dominantBaseline="middle" textAnchor="middle">
                {ZONE_LABELS[z.zone]}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
